import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, X, CheckCircle, Calendar, Plus, ChevronRight } from 'lucide-react';
import { expertDashboardApi, getErrorMessage, GoogleCalendar } from '../../lib/http';

type Step = 'connecting' | 'select-calendar' | 'creating' | 'success';

const GoogleCalendarCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [calendars, setCalendars] = useState<GoogleCalendar[]>([]);
  const [selectedCalendarName, setSelectedCalendarName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Step 1: Exchange OAuth code
  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError('Google authorization was cancelled or failed.');
      return;
    }

    if (code) {
      const redirectUri = `${window.location.origin}/app/integrations/google-calendar/callback`;

      expertDashboardApi.integrations.connectGoogle({
        code,
        redirect_uri: redirectUri,
      })
        .then(() => {
          // After successful connection, fetch available calendars
          return expertDashboardApi.integrations.listGoogleCalendars();
        })
        .then((response) => {
          setCalendars(response.data.calendars || []);
          setStep('select-calendar');
        })
        .catch((err) => {
          setError(getErrorMessage(err));
        });
    } else {
      setError('No authorization code received from Google.');
    }
  }, [searchParams]);

  // Handle calendar selection
  const handleSelectCalendar = async (calendar: GoogleCalendar) => {
    setIsLoading(true);
    try {
      await expertDashboardApi.integrations.selectCalendar(calendar.id);
      setSelectedCalendarName(calendar.summary);
      setStep('success');
      setTimeout(() => navigate('/app/integrations'), 2000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle create new calendar
  const handleCreateCalendar = async () => {
    setStep('creating');
    try {
      const response = await expertDashboardApi.integrations.createGoogleCalendar('BookDay');
      setSelectedCalendarName(response.data.calendar.summary);
      setStep('success');
      setTimeout(() => navigate('/app/integrations'), 2000);
    } catch (err) {
      setError(getErrorMessage(err));
      setStep('select-calendar');
    }
  };

  // Handle skip
  const handleSkip = () => {
    navigate('/app/integrations');
  };

  // Error State
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 max-w-md w-full text-center animate-in fade-in duration-300">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Connection Failed</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/app/integrations')}
            className="w-full px-6 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-colors"
          >
            Back to Integrations
          </button>
        </div>
      </div>
    );
  }

  // Connecting State
  if (step === 'connecting') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center animate-in fade-in duration-300">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl bg-white shadow-lg shadow-slate-200/50 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
            </div>
          </div>
          <p className="text-slate-600 font-medium">Connecting Google Calendar...</p>
        </div>
      </div>
    );
  }

  // Creating Calendar State
  if (step === 'creating') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center animate-in fade-in duration-300">
          <div className="w-20 h-20 rounded-2xl bg-white shadow-lg shadow-slate-200/50 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
          </div>
          <p className="text-slate-600 font-medium">Creating your BookDay calendar...</p>
        </div>
      </div>
    );
  }

  // Success State
  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 max-w-md w-full text-center animate-in fade-in zoom-in-95 duration-300">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">All Set!</h2>
          <p className="text-slate-600 mb-2">
            Your bookings will sync to
          </p>
          <p className="text-purple-600 font-semibold mb-4">"{selectedCalendarName}"</p>
          <p className="text-sm text-slate-400">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Select Calendar State
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 sm:p-8 max-w-md w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-emerald-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-1">Connected!</h2>
          <p className="text-slate-500 text-sm">
            Choose a calendar for your bookings
          </p>
        </div>

        {/* Calendar List */}
        <div className="space-y-2 mb-4 max-h-[280px] overflow-y-auto">
          {calendars.map((calendar) => (
            <button
              key={calendar.id}
              onClick={() => handleSelectCalendar(calendar)}
              disabled={isLoading}
              className="w-full p-3.5 text-left rounded-xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/50 transition-all duration-200 flex items-center gap-3 disabled:opacity-50 group"
            >
              <div
                className="w-4 h-4 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                style={{ backgroundColor: calendar.background_color }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 flex items-center gap-2">
                  <span className="truncate">{calendar.summary}</span>
                  {calendar.primary && (
                    <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-semibold flex-shrink-0">
                      Primary
                    </span>
                  )}
                </div>
                {calendar.description && (
                  <div className="text-xs text-slate-400 truncate mt-0.5">
                    {calendar.description}
                  </div>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-purple-500 transition-colors flex-shrink-0" />
            </button>
          ))}

          {calendars.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm">
              No calendars found with write access.
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-xs text-slate-400 uppercase tracking-wide">or</span>
          </div>
        </div>

        {/* Create New Calendar Button */}
        <button
          onClick={handleCreateCalendar}
          disabled={isLoading}
          className="w-full p-4 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-purple-200 disabled:opacity-50 group"
        >
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
            <Plus className="w-5 h-5" />
          </div>
          <span>Create "BookDay" calendar</span>
        </button>

        <p className="text-xs text-slate-400 text-center mt-3">
          A dedicated calendar keeps your bookings organized
        </p>

        {/* Skip Link */}
        <button
          onClick={handleSkip}
          className="w-full mt-5 text-sm text-slate-400 hover:text-slate-600 transition-colors"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default GoogleCalendarCallback;
